import { useMutation, useQuery } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  GET_SESSIONS_CONFIGURATION_QUERY,
  UPDATE_SESSIONS_CONFIGURATION_MUTATION
} from '../../graphql/iamSessions'
import { getTranslate } from '../../localization'
import '../../types/adminConfig'
import { SessionConfiguration } from '../../types/iamSessions'
import AccountLockout from './components/AccountLockout'
import IPAccessRules from './components/IPAccessRules'
import PasswordComplexity from './components/PasswordComplexity'
import SessionConfigurationPart from './components/SessionConfiguration'
import TwoFactorAuthentication from './components/TwoFactorAuthentication'
import useStyles from './styles/index.styles'

export default function AdminConfigSecurityTab() {
  const classes = useStyles()

  const [updateSessionConfigInput, setUpdateSessionConfigInput] =
    useState<SessionConfiguration>({
      accessTokenExpiryMinutes: 0,
      forceLogoutOnPermissionChange: false,
      sessionIdleTimeoutMinutes: 0,
      lockDurationMinutes: 0,
      maxLoginAttempts: 0,
      permenetLockAterAttempts: 0,
      max2faAttempts: 0,
      maxSessionsPerUser: 0,
      refreshTokenExpiryMinutes: 0,
      require2faForLogin: false,
      sessionAbsoluteTimeoutMinutes: 0,
      webAccessTokenExpiryMinutes: 0,
      otpCoolDownSeconds: 0,
      otpMaxResendSeconds: 0,
      otpTTLSeconds: 0,
      pending2FASessionSecond: 0,
      authenticationHistoryRetentionDays: 365
    })
  const isButtonDisabled =
    updateSessionConfigInput.accessTokenExpiryMinutes <= 0 ||
    updateSessionConfigInput.sessionIdleTimeoutMinutes <= 0 ||
    updateSessionConfigInput.max2faAttempts <= 0 ||
    updateSessionConfigInput.maxSessionsPerUser <= 0 ||
    updateSessionConfigInput.refreshTokenExpiryMinutes <= 0 ||
    updateSessionConfigInput.sessionAbsoluteTimeoutMinutes <= 0 ||
    updateSessionConfigInput.webAccessTokenExpiryMinutes <= 0 ||
    updateSessionConfigInput.authenticationHistoryRetentionDays < 1 ||
    updateSessionConfigInput.authenticationHistoryRetentionDays > 3650

  const { refetch: refetchSessionConfig } = useQuery<{
    sessionConfiguration: SessionConfiguration
  }>(GET_SESSIONS_CONFIGURATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setUpdateSessionConfigInput({
        accessTokenExpiryMinutes:
          res.sessionConfiguration.accessTokenExpiryMinutes,
        refreshTokenExpiryMinutes:
          res.sessionConfiguration.refreshTokenExpiryMinutes,
        sessionIdleTimeoutMinutes:
          res.sessionConfiguration.sessionIdleTimeoutMinutes,
        forceLogoutOnPermissionChange:
          res.sessionConfiguration.forceLogoutOnPermissionChange,
        max2faAttempts: res.sessionConfiguration.max2faAttempts,
        maxSessionsPerUser: res.sessionConfiguration.maxSessionsPerUser,
        require2faForLogin: res.sessionConfiguration.require2faForLogin,
        sessionAbsoluteTimeoutMinutes:
          res.sessionConfiguration.sessionAbsoluteTimeoutMinutes,
        lockDurationMinutes: res.sessionConfiguration.lockDurationMinutes,
        maxLoginAttempts: res.sessionConfiguration.maxLoginAttempts,
        permenetLockAterAttempts:
          res.sessionConfiguration.permenetLockAterAttempts,
        webAccessTokenExpiryMinutes:
          res.sessionConfiguration.webAccessTokenExpiryMinutes,
        otpTTLSeconds: res.sessionConfiguration.otpMaxResendSeconds,
        otpCoolDownSeconds: res.sessionConfiguration.otpCoolDownSeconds,
        otpMaxResendSeconds: res.sessionConfiguration.otpMaxResendSeconds,
        pending2FASessionSecond:
          res.sessionConfiguration.pending2FASessionSecond,
        authenticationHistoryRetentionDays:
          res.sessionConfiguration.authenticationHistoryRetentionDays
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [updateSessionConfig, { loading: loadingForUpdateSession }] =
    useMutation(UPDATE_SESSIONS_CONFIGURATION_MUTATION, {
      onCompleted: (data) => {
        toast.success(data.updateSessionConfiguration.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })
  return (
    <div className={classes.root}>
      <PasswordComplexity />
      <TwoFactorAuthentication
        updateSessionConfigInput={updateSessionConfigInput}
        setUpdateSessionConfigInput={setUpdateSessionConfigInput}
      />
      <AccountLockout
        updateSessionConfigInput={updateSessionConfigInput}
        setUpdateSessionConfigInput={setUpdateSessionConfigInput}
      />
      <SessionConfigurationPart
        updateSessionConfigInput={updateSessionConfigInput}
        setUpdateSessionConfigInput={setUpdateSessionConfigInput}
      />
      <IPAccessRules />
      <div className={classes.btnContainer}>
        <Button
          variant="outlined"
          onClick={() => {
            refetchSessionConfig()
          }}>
          <Typography variant="h4" className={classes.btnText}>
            {getTranslate('Reset')}
          </Typography>
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={isButtonDisabled || loadingForUpdateSession}
          onClick={() => {
            updateSessionConfig({
              variables: { input: updateSessionConfigInput }
            })
          }}
          className={classes.saveButton}>
          <>
            <Typography variant="h4" className={classes.whiteText}>
              {getTranslate('Save Session Settings')}
            </Typography>
            {loadingForUpdateSession && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
              />
            )}
          </>
        </Button>
      </div>
    </div>
  )
}
